import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  getAppointments,
  getAllAppointments,
  getAppointmentDetail,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  getAppointmentSettings,
  updateAppointmentSettings,
  createAppointmentTask,
  completeAppointmentTask,
  createAppointmentNote,
} from '../api';
import { toCalendarEvent, normalizeAppointmentSettings } from '../utils';

export const appointmentKeys = {
  root: (companyId) => ['appointments', companyId],
  list: (companyId, params) => ['appointments', companyId, 'list', params],
  all: (companyId, params) => ['appointments', companyId, 'all', params],
  detail: (companyId, appointmentId) => [
    'appointments',
    companyId,
    'detail',
    appointmentId,
  ],
  settings: (companyId) => ['appointments', companyId, 'settings'],
};

export function useInvalidateAppointments(companyId) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: appointmentKeys.root(companyId),
    });
}

export function useAppointments(companyId, params = {}, options = {}) {
  return useQuery({
    queryKey: appointmentKeys.list(companyId, params),
    queryFn: () => getAppointments(companyId, params),
    enabled: !!companyId && options.enabled !== false,
    ...options,
  });
}

/**
 * Loads all pages and maps to calendar events.
 */
export function useCalendarAppointments(companyId, params = {}, options = {}) {
  return useQuery({
    queryKey: appointmentKeys.all(companyId, params),
    queryFn: async () => {
      const results = await getAllAppointments(companyId, params);
      return results.map(toCalendarEvent).filter((e) => e.dayKey);
    },
    enabled: !!companyId && options.enabled !== false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useAppointmentDetail(
  { companyId, appointmentId },
  options = {},
) {
  return useQuery({
    queryKey: appointmentKeys.detail(companyId, appointmentId),
    queryFn: () => getAppointmentDetail({ companyId, appointmentId }),
    enabled: !!companyId && !!appointmentId && options.enabled !== false,
    ...options,
  });
}

export function useAppointmentSettings(companyId, options = {}) {
  const { select: userSelect, ...restOptions } = options;
  return useQuery({
    queryKey: appointmentKeys.settings(companyId),
    queryFn: () => getAppointmentSettings(companyId),
    select: (data) => {
      const normalized = normalizeAppointmentSettings(data);
      return userSelect ? userSelect(normalized) : normalized;
    },
    enabled: !!companyId && restOptions.enabled !== false,
    ...restOptions,
  });
}

export function useCreateAppointment(companyId) {
  const invalidate = useInvalidateAppointments(companyId);
  return useMutation({
    mutationFn: (payload) => createAppointment({ companyId, payload }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAppointment(companyId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, payload }) =>
      updateAppointment({ companyId, appointmentId, payload }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: appointmentKeys.root(companyId),
      });
      if (variables?.appointmentId) {
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.detail(companyId, variables.appointmentId),
        });
      }
    },
  });
}

export function useDeleteAppointment(companyId) {
  const invalidate = useInvalidateAppointments(companyId);
  return useMutation({
    mutationFn: (appointmentId) =>
      deleteAppointment({ companyId, appointmentId }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAppointmentSettings(companyId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => updateAppointmentSettings({ companyId, payload }),
    onSuccess: (data) => {
      if (data && typeof data === 'object') {
        queryClient.setQueryData(appointmentKeys.settings(companyId), (prev) =>
          normalizeAppointmentSettings({ ...(prev || {}), ...data }),
        );
      }
      queryClient.invalidateQueries({
        queryKey: appointmentKeys.settings(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: appointmentKeys.root(companyId),
      });
    },
  });
}

export function useCreateAppointmentTask(companyId, appointmentId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, payload }) =>
      createAppointmentTask({ companyId, contactId, payload }),
    onSuccess: () => {
      if (appointmentId) {
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.detail(companyId, appointmentId),
        });
      }
    },
  });
}

export function useCompleteAppointmentTask(companyId, appointmentId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, taskId }) =>
      completeAppointmentTask({ companyId, contactId, taskId }),
    onSuccess: () => {
      if (appointmentId) {
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.detail(companyId, appointmentId),
        });
      }
    },
  });
}

export function useCreateAppointmentNote(companyId, appointmentId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, payload }) =>
      createAppointmentNote({ companyId, contactId, payload }),
    onSuccess: () => {
      if (appointmentId) {
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.detail(companyId, appointmentId),
        });
      }
    },
  });
}

export {
  useCalendarStateSnapshot,
  clearCalendarPersistence,
} from './useCalendarStateSnapshot';
